/**
 * Generic parameterizable storage factories for window position and dimensions.
 *
 * Provides factory functions that create localStorage-backed storage instances
 * with dynamic keys, enabling multiple independent windows to persist their
 * state without conflicts.
 */

/** Position data stored in localStorage. */
export type StoredPosition = {
  x: number;
  y: number;
};

/** Dimensions data stored in localStorage. */
export type StoredDimensions = {
  width: number;
  height: number;
};

/** Storage interface for position data. */
export type PositionStorage = {
  save: (position: StoredPosition) => void;
  get: () => StoredPosition | null;
  clear: () => void;
};

/** Storage interface for dimensions data. */
export type DimensionsStorage = {
  save: (dimensions: StoredDimensions) => void;
  get: () => StoredDimensions | null;
  clear: () => void;
};

/**
 * Builds the full localStorage key by prefixing with the environment-aware namespace.
 *
 * In DEV mode the key is prefixed with `fiinder_dev_`, otherwise with `fiinder_`.
 */
function buildStorageKey({ key }: { key: string }): string {
  const prefix = import.meta.env.DEV ? "fiinder_dev_" : "fiinder_";
  return `${prefix}${key}`;
}

/**
 * Creates a position storage instance backed by localStorage.
 *
 * The storage validates that retrieved data contains numeric `x` and `y` fields.
 * All operations silently fail when localStorage is unavailable or throws.
 */
export function createPositionStorage({
  key,
}: {
  key: string;
}): PositionStorage {
  const storageKey = buildStorageKey({ key });

  return {
    save(position: StoredPosition): void {
      /** Persists position to localStorage as JSON. */
      try {
        localStorage.setItem(storageKey, JSON.stringify(position));
      } catch {
        // Silently fail if localStorage unavailable or quota exceeded
      }
    },

    get(): StoredPosition | null {
      /** Retrieves and validates position from localStorage. */
      try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) return null;

        const parsed = JSON.parse(stored);
        if (
          !parsed ||
          typeof parsed.x !== "number" ||
          typeof parsed.y !== "number"
        ) {
          return null;
        }
        return parsed as StoredPosition;
      } catch {
        return null;
      }
    },

    clear(): void {
      /** Removes position data from localStorage. */
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Silently fail
      }
    },
  };
}

/**
 * Creates a dimensions storage instance backed by localStorage.
 *
 * The storage validates that retrieved data contains positive numeric `width`
 * and `height` fields.
 * All operations silently fail when localStorage is unavailable or throws.
 */
export function createDimensionsStorage({
  key,
}: {
  key: string;
}): DimensionsStorage {
  const storageKey = buildStorageKey({ key });

  return {
    save(dimensions: StoredDimensions): void {
      /** Persists dimensions to localStorage as JSON. */
      try {
        localStorage.setItem(storageKey, JSON.stringify(dimensions));
      } catch {
        // Silently fail if localStorage unavailable or quota exceeded
      }
    },

    get(): StoredDimensions | null {
      /** Retrieves and validates dimensions from localStorage. */
      try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) return null;

        const parsed = JSON.parse(stored);
        if (
          !parsed ||
          typeof parsed.width !== "number" ||
          typeof parsed.height !== "number" ||
          parsed.width <= 0 ||
          parsed.height <= 0
        ) {
          return null;
        }
        return parsed as StoredDimensions;
      } catch {
        return null;
      }
    },

    clear(): void {
      /** Removes dimensions data from localStorage. */
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Silently fail
      }
    },
  };
}
